// Presentation helpers for the ember end-of-run scorecard (see run_summary.ts). Split out so they
// can be unit-tested without pulling in the panel's pixi/paperdoll dependencies.

// How a creature kind from the server is presented. Keys are what ember-controller.js reports: a
// wave enemy's orchestrator role, a wilderness enemy's race, or "rabbit". Anything unknown still
// renders (title-cased, with the generic skull) so a new creature type needs no client change.
const CREATURE_META: Record<string, { label: string; emoji: string }> = {
    skirmisher: { label: "Skirmishers", emoji: "💀" },
    raider: { label: "Raiders", emoji: "🗡️" },
    archer: { label: "Archers", emoji: "🏹" },
    bruiser: { label: "Bruisers", emoji: "🧟" },
    eliteRaider: { label: "Elite raiders", emoji: "👹" },
    slime: { label: "Slimes", emoji: "🟢" },
    snake: { label: "Snakes", emoji: "🐍" },
    redspider: { label: "Spiders", emoji: "🕷️" },
    rabbit: { label: "Rabbits", emoji: "🐇" },
}

export const creatureMeta = (key: string): { label: string; emoji: string } =>
    CREATURE_META[key] || { label: key.charAt(0).toUpperCase() + key.slice(1), emoji: "💀" }

// m:ss, or h:mm:ss for the rare long run.
export const formatDuration = (ms: number): string => {
    const total = Math.max(0, Math.round(ms / 1000))
    const hours = Math.floor(total / 3600)
    const minutes = Math.floor((total % 3600) / 60)
    const seconds = total % 60
    const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`)
    return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`
}

// What the panel needs to know about the camp's lives, in one place: how many embers to draw, how
// many of them are burnt out, what to write underneath, and — the load-bearing part — whether this
// camp will have the player back at all.
//
// A life is spent by DYING, so `spent` already counts the death that put this panel up. A life is
// what buys "Try again" (get up in this camp and carry the run on), so once they are all spent the
// camp is finished with the player and only "Restart" — a fresh camp, always available — is left.
// The server enforces the same rule; this is what the panel DRAWS, not what decides it.
export interface LivesView {
    total: number
    spent: number
    remaining: number
    canTryAgain: boolean
    caption: string
}

export const livesView = (summary: { livesTotal?: number; livesSpent?: number }): LivesView => {
    // A summary from a camp that doesn't report lives at all (an older server) reads as "no lives
    // to show" rather than as "out of lives": the panel then hides the embers and leaves both ways
    // on available, which is how it behaved before lives existed.
    const total = Math.max(0, Math.floor(summary?.livesTotal || 0))
    const spent = Math.max(0, Math.min(total, Math.floor(summary?.livesSpent || 0)))
    const remaining = total - spent
    return {
        total,
        spent,
        remaining,
        canTryAgain: total === 0 || remaining > 0,
        caption: total === 0 ? "" : remaining > 0 ? `${spent} OF ${total} SPENT` : "ALL SPENT",
    }
}

// The ways on the panel offers, in the order they are drawn. Which buttons EXIST is the rule this
// panel is really enforcing, so it lives here, in the half that can be tested without pixi: the
// widget only decides how wide they are and where they sit.
//
// While the camp has a life left there are two, with "try again" lit as the primary one. Once it
// hasn't, there is exactly ONE -- the spent option is not drawn at all rather than drawn dead,
// because a greyed-out button teaches nothing the burnt-out embers above it haven't already said
// and leaves the player hunting for which half of the panel still works.
export type RunSummaryAction = "tryAgain" | "restart"

export interface RunSummaryButton {
    action: RunSummaryAction
    label: string
    sublabel: string
    primary: boolean
}

const RESTART_BUTTON = { action: "restart" as RunSummaryAction, label: "RESTART", sublabel: "A FRESH CAMP" }

export const runSummaryButtons = (summary: { livesTotal?: number; livesSpent?: number }): RunSummaryButton[] => {
    if (!livesView(summary).canTryAgain) {
        return [{ ...RESTART_BUTTON, primary: true }]
    }
    return [
        { action: "tryAgain", label: "TRY AGAIN", sublabel: "HOLD THIS CAMP", primary: true },
        { ...RESTART_BUTTON, primary: false },
    ]
}

